/**
 * Wiring a live Voice SDK `Call` into the call state, whichever direction
 * produced it.
 *
 * Moved out of `CallProvider.tsx` unchanged when that file passed AGENTS.md §4's
 * limit. The provider decides WHEN there is a call - dialling, answering, hanging
 * up - and this file says what each of the SDK's events does to the state once
 * there is one. The two network-warning refs came with it, because nothing else
 * reads them.
 */
import { useCallback, useRef, type Dispatch, type MutableRefObject, type SetStateAction } from "react";
import type { Call } from "@twilio/voice-sdk";
import { forgetRemoteAudio, trackRemoteAudio } from "./audio/speaker";
import { callErrorTag, classifyCallError } from "./callError";
import type { CallState } from "./callState";
import { isNetworkWarning, linkFrom } from "./linkQuality";

/** Wire one live `Call`. `update` carries the caller's own session guard. */
export type AttachCall = (call: Call, live: () => boolean, update: (patch: Partial<CallState>) => void) => void;

/** The provider's own refs and setter, which the handlers read and write. */
export interface CallEventRefs {
  /** The running call, let go of the moment it is over. */
  callRef: MutableRefObject<Call | null>;
  /** Is the running call a conference? Written by the provider before `connect`. */
  conferenceRef: MutableRefObject<boolean>;
  /** Lets the `Device` go once the call is over. */
  releaseRef: MutableRefObject<() => void>;
  setState: Dispatch<SetStateAction<CallState>>;
}

export function useCallEvents({ callRef, conferenceRef, releaseRef, setState }: CallEventRefs): AttachCall {
  /**
   * The network warnings standing on THIS call, and whether the SDK is rebuilding
   * the media connection.
   *
   * Refs for the same reason `conferenceRef` is one: the handlers in `attach`
   * are built once with empty deps and would otherwise close over the first
   * render's values forever. Two of them rather than one grade, because the
   * grade is derived - see `linkQuality.ts`, which explains why merging these
   * two into a single boolean produces a strip that goes out on a line that is
   * still bad.
   */
  const warningsRef = useRef<Set<string>>(new Set());
  const reconnectingRef = useRef(false);

  /**
   * Wire up a live `Call`, whichever direction produced it.
   *
   * Everything from here on is identical for a call we placed and a call we
   * answered, which is why it is one function: the mute button, the timer, the
   * hangup and the co-pilot all read the same object.
   */
  return useCallback(
    (call: Call, live: () => boolean, update: (patch: Partial<CallState>) => void) => {
      callRef.current = call;
      // Nothing carried over from the call before this one. The SDK builds a
      // fresh `<audio>` element per call, so anything still held here is a dead
      // node from a conversation that is over.
      forgetRemoteAudio();
      // Nor anything the LAST line was complaining about. Both have to be reset
      // here rather than on hangup: a call that dies WHILE reconnecting fires
      // `reconnecting` and then `disconnect` with no `reconnected` in between,
      // so the flag would still be standing when the next call opens and the
      // operator would dial into a window that already says the line is gone.
      warningsRef.current.clear();
      reconnectingRef.current = false;
      // The far end's volume, applied to the element the SDK plays it through.
      // It fires again mid-call whenever the operator switches speakers, since
      // a new sink means a new element - see `audio/speaker.ts`.
      call.on("audio", trackRemoteAudio);
      call.on("ringing", () => update({ phase: "ringing" }));
      call.on("accept", () => {
        // On a conference, `accept` means the browser joined an EMPTY room -
        // the customer's phone has not even rung yet. Promoting to "active"
        // here would start the talk timer against several seconds of one-way
        // silence, which `answerOnBridge="true"` used to prevent by deferring
        // this very event until after the whisper. Stay on "ringing" until the
        // bridge poll reports the customer actually entered the room.
        //
        // Read through a ref, not `state.conference`: this callback is created
        // once (the useCallback below has empty deps, shared by both
        // directions) and would close over the initial value forever.
        if (conferenceRef.current) {
          update({ bridged: false });
          return;
        }
        update({ phase: "active", answeredAt: Date.now() });
      });
      call.on("mute", (muted: boolean) => update({ muted }));
      /**
       * The line's own health, which nothing used to listen to.
       *
       * `update` already carries the session guard, so a warning arriving late
       * from a call that is over is dropped for free. The grade is recomputed
       * from both refs on every event rather than patched in place: a warning
       * clearing while a reconnect is still running must not read as recovery.
       */
      const pushLink = () =>
        update({ link: linkFrom(warningsRef.current, reconnectingRef.current) });
      call.on("warning", (name: string) => {
        // Names only. The same event carries the SDK's audio-level thresholds,
        // and `ice-connectivity-lost` arrives with no threshold payload at all -
        // reading the data object would both mislead and throw.
        if (!isNetworkWarning(name)) return;
        warningsRef.current.add(name);
        pushLink();
      });
      call.on("warning-cleared", (name: string) => {
        if (!warningsRef.current.delete(name)) return;
        pushLink();
      });
      call.on("reconnecting", () => {
        reconnectingRef.current = true;
        pushLink();
      });
      call.on("reconnected", () => {
        reconnectingRef.current = false;
        pushLink();
      });
      call.on("disconnect", () => {
        if (!live()) return;
        setState((prev) => {
          // The poll got here first. Leave its answer alone rather than
          // recomputing: it carries TWILIO's duration, and the local timer that
          // would replace it has been running since the browser last knew
          // anything - which on a call the server had to end is longer than the
          // conversation was.
          if (prev.phase === "ended") return prev;
          // A takeover whose leg dropped before the bridge never became this
          // operator's call: there is nothing to label or write a note on, and
          // the AI is (or was) still the one on it. Its own sentence, not "ended".
          if (prev.takeover && prev.phase !== "active" && prev.phase !== "error") {
            return { ...prev, phase: "error", link: "ok", error: "takeover_interrupted", finalDuration: null };
          }
          return {
            ...prev,
            phase: prev.phase === "error" ? "error" : "ended",
            // A call that dropped WHILE the line was bad would otherwise land on
            // the after-call screen still announcing a weak connection, about a
            // call that is no longer running. The UI also hides the strip once
            // the call is down; both, because either alone is one refactor away
            // from letting it through.
            link: "ok",
            finalDuration: prev.answeredAt
              ? Math.round((Date.now() - prev.answeredAt) / 1000)
              : null,
          };
        });
        callRef.current = null;
        releaseRef.current();
      });
      call.on("error", (err: { code?: number }) => {
        if (!live()) return;
        // NOT EVERY SDK ERROR IS A FAILED CALL, and treating them alike is what
        // made a customer hanging up look like a misconfigured account: the
        // gateway's HANGUP can carry an error payload, the SDK re-emits it as
        // 31005 and disconnects on the next line, and the red screen then won
        // over the ended one (see `disconnect` above, which keeps an error
        // phase deliberately). `classifyCallError` reads the phase out of
        // `prev` for the same reason `onDeviceError` does - `phaseRef` is
        // written from an effect and is a render behind.
        setState((prev) => {
          const verdict = classifyCallError(err?.code, prev.phase, prev.direction);
          if (verdict === "ignore") return prev;
          // On a takeover still on its way in, no error is the settings and none
          // is an ordinary ending: the AI's call was fine, and this browser's leg
          // did not make it. The microphone keeps its own sentence, because that
          // one the operator can fix.
          if (prev.takeover && prev.phase !== "active") {
            const error = err?.code === 31401 ? "mic_denied" : "takeover_interrupted";
            return { ...prev, phase: "error", link: "ok", error, finalDuration: null };
          }
          if (verdict === "ended") {
            return {
              ...prev,
              phase: "ended",
              // See `disconnect` above: a grade about a running line has nothing
              // to say once there is no line.
              link: "ok",
              finalDuration: prev.answeredAt
                ? Math.round((Date.now() - prev.answeredAt) / 1000)
                : null,
            };
          }
          return { ...prev, phase: "error", link: "ok", error: callErrorTag(verdict, err?.code) };
        });
        // Unconditional, including behind an ignored error: on a call that is
        // already over there is nothing left to let go of, and both of these
        // are no-ops the second time round.
        callRef.current = null;
        releaseRef.current();
      });
    },
    [],
  );
}
